/**
 * Thin fetch wrapper for calling our backend API.
 *
 * - Always sends cookies (credentials: 'include') so the session cookie
 *   reaches the backend
 * - Parses the ApiResponse envelope and throws on error shape
 * - Small surface area on purpose; add more behaviour (retries, caching)
 *   in TanStack Query layers, not here
 */

import type { ApiResponse, ApiError } from '@loanaudit/shared';

const BASE = import.meta.env.VITE_API_URL || '';

export class ApiClientError extends Error {
  public readonly code: string;
  public readonly httpStatus: number;
  public readonly details: Record<string, unknown> | undefined;
  constructor(httpStatus: number, err: ApiError) {
    super(err.message);
    this.code = err.code;
    this.httpStatus = httpStatus;
    this.details = err.details;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = opts;

  let url = `${BASE}${path}`;
  if (query) {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null) params.set(k, String(v));
    });
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }

  const res = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  let parsed: ApiResponse<T>;
  try {
    parsed = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiClientError(res.status, {
      code: 'INVALID_RESPONSE',
      message: `Server returned non-JSON response (${res.status})`,
    });
  }

  if (!parsed.ok) {
    throw new ApiClientError(res.status, parsed.error);
  }
  return parsed.data;
}
